import { Injectable } from '@angular/core';
import { Logger, LoggerFactory } from '../core/logger.service';
import { WidgetComponent, WidgetState } from './widgets.model';

@Injectable({ providedIn: 'root' })
export class WidgetComponentsService {
  private readonly logger: Logger;

  constructor(readonly loggerFactory: LoggerFactory) {
    this.logger = loggerFactory.get('WidgetComponentsService');
  }

  /**
   * Get parent recursively (from top-level or child or grandchild)
   */
  public static getParentComponent(state: WidgetState, parentId: number): WidgetComponent {
    for (const container of state.containers || []) {
      let parent: WidgetComponent = container.components.find((x) => x.id === parentId); // Check top-level
      if (parent) {
        return parent;
      }
      if (!parent) {
        for (const component of container.components) {
          parent = component.children?.find((child) => child.id === parentId); // Check children
          if (parent) {
            return parent;
          }
        }
      }
    }
    return undefined;
  }

  /**
   * Get first/no component matching provided ID recursively (top-level, children, grandchildren) with it's siblings
   * @returns Component matching provided ID and array of components (siblings and self)
   */
  public static getComponentByIdWithSiblings(
    state: WidgetState,
    componentId: number,
  ): { component: WidgetComponent; siblings: WidgetComponent[] } {
    if (!state) {
      return undefined;
    }

    // Check regular containers
    for (const container of state.containers || []) {
      for (const component of container.components || []) {
        // Check top-level
        if (component.id === componentId) {
          return { component: component, siblings: container.components };
        }
        for (const child of component.children || []) {
          // Check child
          if (child.id === componentId) {
            return { component: child, siblings: component.children };
          }
          for (const grandChild of child.children || []) {
            // Check grand child
            if (grandChild.id === componentId) {
              return { component: grandChild, siblings: child.children };
            }
          }
        }
      }
    }

    // Check floating container
    for (const component of state.floatingContainer?.components || []) {
      if (component.id === componentId) {
        return { component: component, siblings: state.floatingContainer.components };
      }
    }
    return undefined; // No match
  }

  /**
   * Get first/no component matching provided ID recursively (top-level, children, grandchildren)
   */
  public static getComponentById(state: WidgetState, componentId: number): WidgetComponent {
    return this.getComponentByIdWithSiblings(state, componentId)?.component;
  }

  /**
   * Get components, including children and grandchildren, flattened in top-level
   */
  public static getComponentsFlattened(
    state: WidgetState,
    includeFloatingContainerComponents?: boolean,
  ): WidgetComponent[] {
    let components =
      state.containers.flatMap((container) => container.components.flatMap(this.flattenComponentChildren)) || [];
    if (includeFloatingContainerComponents) {
      components = components.concat(state.floatingContainer.components);
    }
    return components;
  }

  private static flattenComponentChildren = (component: WidgetComponent): WidgetComponent[] => {
    return [component, ...(component.children?.flatMap(WidgetComponentsService.flattenComponentChildren) ?? [])];
  };

  /**
   * Set relations properties on eventual children and grandchildren
   */
  public static setChildrenProperties(component: WidgetComponent, containerId: number) {
    if (component.children) {
      component.children.sort((a, b) => a.order - b.order); // TODO: Let back-end handle sorting (later)
      component.children.forEach((child: WidgetComponent) => {
        this.setChildProperties(child, component, containerId);
        if (child.children?.length > 0) {
          child.children.forEach((grandChild: WidgetComponent) => {
            this.setChildProperties(grandChild, child, containerId, component);
          });
        }
      });
    }
  }

  private static setChildProperties(
    child: WidgetComponent,
    parent: WidgetComponent,
    containerId: number,
    grandparent?: WidgetComponent,
  ) {
    child.containerId = containerId;
    child.parentId = parent.id;
    child.parentComponentType = parent.type;
    child.parentComponentSubType = parent.subType;
    child.parentBackground = parent.background;
    child.grandparentId = grandparent?.id;
    child.grandparentSubType = grandparent?.subType;
  }

  /**
   * TODO
   * @param componentArray
   * @param component
   * @param index
   * @returns
   */
  public static refreshWidgetComponentProperties(
    componentArray: WidgetComponent[],
    component: WidgetComponent,
    index: number,
  ): void {
    if (!component) {
      //this.logger.warn() // TODO: Log. But need to make this method none static
      return;
    }
    const keysToExclude = ['$type', 'id', 'type', 'subType', 'children'];
    const objectKeys = Object.keys(component).filter((key) => keysToExclude.indexOf(key) === -1);
    const targetObjectKeys = Object.keys(componentArray[index])
      .filter((key) => keysToExclude.indexOf(key) === -1)
      .filter((x) => objectKeys.indexOf(x) === -1);
    const mergedKeys = objectKeys.concat(targetObjectKeys);
    for (const key of mergedKeys) {
      componentArray[index][key] = component[key];
    }
  }
}
